var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable camelcase */
/* eslint-disable @typescript-eslint/naming-convention */
import React, { useState, useCallback, useMemo, useEffect, useLayoutEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { translate } from '@utils/i18n';
import { Button } from '@ui';
import { ConfirmDialog } from '@ui/molecules/ConfirmDialog';
import { Brushaway } from '@ui/molecules/Brushaway/Brushaway';
import { BrushawayHeader } from '@ui/molecules/Brushaway/BrushawayHeader';
import { BrushawayBody } from '@ui/molecules/Brushaway/BrushawayBody';
import { AiConfigureMenu, buildAiConfigureItems } from '@features/AiConfigureMenu';
import { ChatWidgetDrawer } from './components/ChatWidgetDrawer';
import { ChatWidgetConversation } from './components/ChatWidgetConversation';
import { useChatHistory } from './hooks/useChatHistory';
import { useBlockStream } from './hooks/useBlockStream';
import { useChatInput } from './hooks/useChatInput';
import { useFileUpload } from './hooks/useFileUpload';
import { useChatMessages } from './hooks/useChatMessages';
import { useConfirmation } from './hooks/useConfirmation';
import { useAiToken } from './hooks/useAiToken';
import { usePageContext } from './hooks/usePageContext';
import { isTerritorySuppressed } from './services/pageContextValidator';
import { useLiveListContext } from './hooks/useLiveListContext';
import { useTosStatusQuery } from './hooks/useTosStatusQuery';
import { useAcceptTosMutation } from './hooks/useAcceptTosMutation';
import { createChat, submitFeedback, cancelResponse, submitUndo, pollUndoUntilSettled, describeChatSendError } from './services/chatApi';
import { ChatWidgetContextProvider, useChatWidgetContext } from './contexts/ChatWidgetContext';
import { AITriggerButton } from './components/AITriggerButton';
import { buildUserMessage, buildAgentMessage, DEFAULT_AGENT } from './helpers';
import { buildLiveAgentMessage, buildStreamEndMessage, buildCancelledMessage } from './helpers/streamHelpers';
import { resolveTosGate, TOS_STATUS_UNAVAILABLE_MESSAGE } from './helpers/tosGate';
import './AIChatWidgetStyles.scss';
import { PromptLibraryModal } from './components/PromptLibraryModal';
import { TosModal } from './components/TosModal';
import { CoachWelcomeModal } from './components/CoachWelcomeModal/CoachWelcomeModal';
import { loadWidgetState, saveWidgetState, clearWidgetState, isConversationStale } from './services/widgetStateStorage';
import { conversationScopeForPageContext } from './services/conversationScope';
import { FEEDBACK_TURN_UNKNOWN } from './constants/feedback';
import { deleteChatSession, getChatSessionBySessionId } from './services/chatSessionsApi';
import { subscribeWidgetCommands } from './services/widgetCommandBus';
import { PROMPT_LIBRARY, TECH_PORTAL_PROMPT_LIBRARY } from './constants/promptLibrary';
import { loadTrainingModeEnabled, saveTrainingModeEnabled, loadCoachWelcomeSeen, saveCoachWelcomeSeen } from './constants/trainingMode';
import { acknowledgeCoachWelcome } from './services/coachWelcomeApi';
import { getContextualSuggestions, getContextualSuggestionsLabel, resolvePageType } from './constants/contextualSuggestions';
import { extractTextFromContent } from './helpers/historyHelpers';
import { ackNudge } from './services/nudgeApi';
import { trackEvent } from './services/telemetryApi';
import { NUDGE_ACTION_ACCEPTED, NUDGE_ACTION_DISMISSED } from './constants/nudge';
import { TELEMETRY_EVENT_NUDGE_SHOWN, TELEMETRY_EVENT_NUDGE_ACCEPTED, TELEMETRY_EVENT_NUDGE_DISMISSED, TELEMETRY_EVENT_CHIP_CLICK, TELEMETRY_EVENT_COACH_WELCOME_SHOWN, TELEMETRY_EVENT_COACH_WELCOME_COMPLETED, TELEMETRY_EVENT_COACH_WELCOME_DISMISSED } from './constants/telemetry';
const HEADER_NAMESPACE = 'features.ai_chat_widget.header';
const DELETE_CONVERSATION_NAMESPACE = 'features.ai_chat_widget.delete_conversation';
const TOS_NAMESPACE = 'features.ai_chat_widget.tos';
function buildOptimisticFileRefs(files) {
    return files.map((file, index) => ({
        file_id: `temp-${Date.now()}-${index}`,
        file_name: file.name,
        mime_type: file.type || 'application/octet-stream',
        file_size_bytes: file.size,
        scan_status: 'pending_scan'
    }));
}
function AIChatWidgetInner({ userName, defaultOpen, presentationMode, pageContext, host, coachWelcomeSeen, isAdmin }) {
    var _a, _b, _c, _d, _e;
    // ---------------------------------------------------------------------------
    // Restore persisted state from localStorage on initial render
    // ---------------------------------------------------------------------------
    const initialConversationScope = conversationScopeForPageContext(pageContext);
    const savedState = loadWidgetState(initialConversationScope);
    // isOpen is intentionally NOT restored from localStorage — opening in a new tab
    // should not auto-expand the widget when it was open elsewhere. Only `defaultOpen`
    // controls the initial visibility. See ip-assistant widget-persistence notes.
    const [isOpen, setIsOpen] = useState(Boolean(defaultOpen));
    const [activeConversationId, setActiveConversationId] = useState((_a = savedState === null || savedState === void 0 ? void 0 : savedState.activeSessionId) !== null && _a !== void 0 ? _a : null);
    const [activeConversationScope, setActiveConversationScope] = useState(initialConversationScope);
    const activeConversationIdRef = useRef(activeConversationId);
    const conversationScopeGenerationRef = useRef(0);
    // Cancels an in-flight queued-undo poll on unmount or conversation switch.
    const undoPollAbortRef = useRef(null);
    useLayoutEffect(() => {
        activeConversationIdRef.current = activeConversationId;
    }, [activeConversationId]);
    // Queued "ingest documents" hand-off from another island (Documents settings
    // page → widget). Held until the widget has reset to a fresh conversation, then
    // dispatched via handleSendMessage so the standard upload+create-chat path runs.
    const [queuedIngest, setQueuedIngest] = useState(null);
    const [, setSelectedAgent] = useState(undefined);
    const [streamingMessageId, setStreamingMessageId] = useState(null);
    const [isPromptLibraryOpen, setIsPromptLibraryOpen] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const { data: tosStatus, isLoading: isTosStatusLoading } = useTosStatusQuery();
    const acceptTosMutation = useAcceptTosMutation();
    const [tosRequiredByServer, setTosRequiredByServer] = useState(false);
    // Three-state, not two: true / false / undefined-because-the-request-failed.
    // See helpers/tosGate.ts for why collapsing these caused a silent dead composer.
    const tosGate = resolveTosGate(tosStatus === null || tosStatus === void 0 ? void 0 : tosStatus.accepted);
    const isTosModalOpen = !isTosStatusLoading && ((tosStatus === null || tosStatus === void 0 ? void 0 : tosStatus.accepted) === false || tosRequiredByServer);
    const isTosAcceptSubmitting = acceptTosMutation.isPending;
    const brushawayRef = useRef(null);
    const { setRetryPrompt, triggerFocusInput } = useChatWidgetContext();
    // Training Mode (ipa-training-mode): prompt coaching + new-conversation
    // nudges. Governed solely by the per-user 🎓 toggle in the header, persisted
    // in localStorage (defaults off). Available to every enabled widget host.
    const [trainingModeUserPref, setTrainingModeUserPref] = useState(() => loadTrainingModeEnabled());
    const trainingMode = trainingModeUserPref;
    // First-enable Coach welcome (ipa-training-mode). Server is the source of
    // truth (prop, cross-device); localStorage is an optimistic fast-path so a
    // dismissal sticks for the session even if the ack write is slow/fails.
    const [hasSeenCoachWelcome, setHasSeenCoachWelcome] = useState(
    // Server is the source of truth: when the host provides the flag (back-office
    // ERB always does; the technician portal passes the token's coach_welcome_seen),
    // trust it — a server "not seen" must be able to re-show the welcome. Only fall
    // back to the localStorage fast-path when the host doesn't pass the flag at all.
    () => (typeof coachWelcomeSeen === 'boolean' ? coachWelcomeSeen : loadCoachWelcomeSeen()));
    const [isCoachWelcomeOpen, setIsCoachWelcomeOpen] = useState(false);
    // Prompt coach toggle now lives in the drawer footer (IP-14858). The ref
    // targets that nav item's underlying button so the Coach welcome can
    // return focus to it on dismiss (a wrapper span isn't focusable).
    const trainingModeBtnRef = useRef(null);
    const promptLibrary = host === 'technician_portal' ? TECH_PORTAL_PROMPT_LIBRARY : PROMPT_LIBRARY;
    // File upload is non-functional on technician portal / iOS WKWebView (native
    // app never implements the file-picker bridge) and the field assistant
    // ignores file content anyway. Hide the affordance there; keep it for
    // back-office where it works. Proper field/iOS support is tracked separately
    // (IP-14663).
    const allowAttachments = host !== 'technician_portal';
    // Focus input whenever the widget opens
    useEffect(() => {
        if (isOpen)
            triggerFocusInput();
    }, [isOpen, triggerFocusInput]);
    // ---------------------------------------------------------------------------
    // Persist widget state to localStorage on changes
    // ---------------------------------------------------------------------------
    useEffect(() => {
        saveWidgetState({
            activeSessionId: activeConversationId
        }, activeConversationScope);
    }, [activeConversationId, activeConversationScope]);
    // ---------------------------------------------------------------------------
    // Validate the stored session on mount and whenever iOS changes object scope.
    // ---------------------------------------------------------------------------
    useEffect(() => {
        let cancelled = false;
        const validateStoredSession = () => __awaiter(this, void 0, void 0, function* () {
            var _a;
            const storedState = loadWidgetState(activeConversationScope);
            const storedSessionId = storedState === null || storedState === void 0 ? void 0 : storedState.activeSessionId;
            if (!storedSessionId)
                return;
            try {
                // Direct lookup by session_id — avoids pagination issues
                const session = yield getChatSessionBySessionId(storedSessionId);
                if (cancelled || activeConversationIdRef.current !== storedSessionId)
                    return;
                // Don't auto-resume a conversation that's been idle too long (e.g. a tab
                // reopened the next day). Drop the user on the new-conversation screen
                // instead; the old thread stays reachable from history.
                if (isConversationStale(session.lastActivityAt, session.createdAt)) {
                    if (process.env.NODE_ENV !== 'production') {
                        console.info('[AIChatWidget] Stored conversation idle past threshold, starting fresh');
                    }
                    clearWidgetState(activeConversationScope);
                    setActiveConversationId(null);
                }
                // Otherwise the session exists and is fresh — no action needed.
            }
            catch (err) {
                if (cancelled || activeConversationIdRef.current !== storedSessionId)
                    return;
                const status = (_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.status;
                if (status === 404) {
                    // Session was deleted, clear stored state
                    if (process.env.NODE_ENV !== 'production') {
                        console.info('[AIChatWidget] Stored session no longer exists, clearing');
                    }
                    clearWidgetState(activeConversationScope);
                    setActiveConversationId(null);
                }
                else if (process.env.NODE_ENV !== 'production') {
                    // Network error — keep session ID, it might come back
                    console.warn('[AIChatWidget] Failed to validate session:', err);
                }
            }
        });
        void validateStoredSession();
        return () => {
            cancelled = true;
        };
    }, [activeConversationScope]);
    // Cross-tab activeConversationId sync was removed deliberately.
    // Previous behavior fired a `storage` event on every conversation switch,
    // forcing all open tabs to re-subscribe to the same thread_id — multiplying
    // SSE/getMessages traffic by tab count. Tabs are now independent: each tab
    // tracks its own active conversation, and `saveWidgetState` only seeds new
    // tabs / refreshes with the most-recent conversation.
    // ---------------------------------------------------------------------------
    // JWT token management — fetch from Rails, auto-refresh every 13 min
    // ---------------------------------------------------------------------------
    const { token: aiToken } = useAiToken({ enabled: isOpen });
    // ---------------------------------------------------------------------------
    // Page context detection — auto-detect from URL, validate against territories
    // Server-rendered pageContext prop is validated through the hook.
    // ---------------------------------------------------------------------------
    const { context: validatedPageContext } = usePageContext(undefined, pageContext);
    // Live listing overlay: the office grids publish their CURRENT page/filter/
    // sort/count/visible-rows to the page-context bus (the server snapshot is
    // computed at HTML render, before the grid loads, so it goes stale the moment
    // the user filters/paginates). When present, the live value replaces the
    // snapshot's list_context so IPA sees exactly what's on screen.
    const liveListContext = useLiveListContext();
    const effectivePageContext = useMemo(() => {
        if (!validatedPageContext)
            return validatedPageContext;
        if (!liveListContext)
            return validatedPageContext;
        // A territory-suppressed context carries only write-authority fields; the
        // live grid summary must not be layered back onto it.
        if (isTerritorySuppressed(validatedPageContext))
            return validatedPageContext;
        return Object.assign(Object.assign({}, validatedPageContext), { list_context: liveListContext });
    }, [validatedPageContext, liveListContext]);
    const requestedConversationScope = useMemo(() => conversationScopeForPageContext(effectivePageContext), [effectivePageContext]);
    // Context-aware suggestion chips — independent of training mode. Every user
    // gets page-aware chips when the current page has curated suggestions;
    // otherwise falls back to the host's generic PROMPT_SUGGESTIONS.
    const effectiveSuggestions = useMemo(() => getContextualSuggestions(effectivePageContext, host), [effectivePageContext, host]);
    const effectiveSuggestionsLabel = useMemo(() => getContextualSuggestionsLabel(effectivePageContext), [effectivePageContext]);
    // ---------------------------------------------------------------------------
    // File upload state
    // ---------------------------------------------------------------------------
    const fileUpload = useFileUpload();
    const pendingMessageRef = useRef(null);
    // ---------------------------------------------------------------------------
    // Conversation list from Rails ai_chat_sessions
    // ---------------------------------------------------------------------------
    const { conversations, isLoading: isLoadingHistory, refetch: refetchConversations } = useChatHistory();
    const queryClient = useQueryClient();
    // ---------------------------------------------------------------------------
    // Message history from orchestrator — single source of truth for messages
    // ---------------------------------------------------------------------------
    const { messages: historyMessages, isLoading: isLoadingMessages, isFetchingNextPage, hasNextPage, fetchNextPage, appendMessage, updateMessage, seedMessages, clearMessages, settleUndo } = useChatMessages(activeConversationId);
    const handleStreamEnd = useCallback((finalState) => {
        var _a;
        // Use activeConversationId as fallback when stream_start was missed (SSE race condition)
        const resolvedResponseId = finalState.responseId || finalState.feedbackId;
        const resolvedConversationId = finalState.conversationId || activeConversationId;
        if (!resolvedResponseId || !resolvedConversationId) {
            setStreamingMessageId(null);
            refetchConversations();
            return;
        }
        // Ignore stale stream_end events from previously active conversations.
        // This prevents cross-conversation message duplication when the user quickly
        // starts a new chat while another response is still streaming.
        if (!activeConversationId || resolvedConversationId !== activeConversationId) {
            refetchConversations();
            return;
        }
        // Skip empty responses (e.g., status-only events from SSE reconnection).
        // A real response should have at least one content block.
        if (finalState.blocks.length === 0) {
            setStreamingMessageId(null);
            refetchConversations();
            return;
        }
        // A replayed current-turn response can arrive after the browser missed
        // stream_start during reconnect. Accept it if it has content, but avoid
        // duplicating a response already appended from an earlier live stream.
        const alreadyRendered = historyMessages.some((message) => { var _a; return message.id === resolvedResponseId || ((_a = message.envelope) === null || _a === void 0 ? void 0 : _a.response_id) === resolvedResponseId; });
        if (alreadyRendered) {
            setStreamingMessageId(null);
            refetchConversations();
            return;
        }
        const agentMessage = buildStreamEndMessage({
            responseId: resolvedResponseId,
            conversationId: resolvedConversationId,
            turnNumber: (_a = finalState.turnNumber) !== null && _a !== void 0 ? _a : FEEDBACK_TURN_UNKNOWN,
            agent: finalState.agent,
            blocks: finalState.blocks,
            suggestedActions: finalState.suggestedActions,
            feedbackId: finalState.feedbackId || '',
            durationMs: finalState.durationMs
        });
        appendMessage(agentMessage);
        setStreamingMessageId(null);
        refetchConversations();
    }, [refetchConversations, appendMessage, activeConversationId, historyMessages]);
    const { state: streamState, abort: abortStream, reset: resetStream, reconnect: reconnectStream, connectionState, reconnectAttempts, clearNudge, wasAuthRefresh } = useBlockStream({
        conversationId: activeConversationId,
        onStreamEnd: handleStreamEnd,
        // IP-15624: an `undo_settled` event flips the already-rendered card for the
        // named tokens to its settled state. The cards live in the message cache
        // (they belong to earlier turns), which useChatMessages owns.
        onUndoSettled: settleUndo
    });
    const handleToggleTrainingMode = useCallback(() => {
        const next = !trainingModeUserPref;
        saveTrainingModeEnabled(next);
        setTrainingModeUserPref(next);
        // First time an eligible actor turns Coach ON: Coach is already on now (above);
        // surface the one-time, non-blocking welcome to explain what it does.
        if (next && !hasSeenCoachWelcome) {
            setIsCoachWelcomeOpen(true);
            trackEvent(TELEMETRY_EVENT_COACH_WELCOME_SHOWN, { conversation_id: activeConversationId });
        }
    }, [trainingModeUserPref, hasSeenCoachWelcome, activeConversationId]);
    // If Coach is turned off while the welcome is still open (e.g. keyboard
    // activation of the toggle, which bypasses the popover's outside-click
    // dismissal), close it silently. The welcome only makes sense while Coach is
    // on; leaving it open would keep the cap button obscured and could re-emit
    // coach_welcome_shown on re-enable. No completion/dismissal telemetry here —
    // the user didn't act on the welcome, they toggled the feature.
    useEffect(() => {
        if (!trainingMode && isCoachWelcomeOpen) {
            setIsCoachWelcomeOpen(false);
        }
    }, [trainingMode, isCoachWelcomeOpen]);
    // Shared teardown for the welcome: mark seen locally (optimistic), persist to
    // the server (fail-open — never blocks chat), and return focus to the cap.
    const finishCoachWelcome = useCallback(() => {
        var _a;
        setIsCoachWelcomeOpen(false);
        setHasSeenCoachWelcome(true);
        saveCoachWelcomeSeen(true);
        void acknowledgeCoachWelcome();
        (_a = trainingModeBtnRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, []);
    const handleCompleteCoachWelcome = useCallback(() => {
        trackEvent(TELEMETRY_EVENT_COACH_WELCOME_COMPLETED, { conversation_id: activeConversationId });
        finishCoachWelcome();
    }, [activeConversationId, finishCoachWelcome]);
    const handleDismissCoachWelcome = useCallback(() => {
        trackEvent(TELEMETRY_EVENT_COACH_WELCOME_DISMISSED, { conversation_id: activeConversationId });
        finishCoachWelcome();
    }, [activeConversationId, finishCoachWelcome]);
    // New-conversation nudge (ipa-training-mode). Only surfaced when training
    // mode is on; the SSE handler stores it regardless (fail-safe — a stray
    // nudge with training mode off is simply never rendered/tracked here).
    const shownNudgeIdRef = useRef(null);
    useEffect(() => {
        const nudge = streamState.pendingNudge;
        if (!trainingMode || !nudge || shownNudgeIdRef.current === nudge.nudge_id)
            return;
        shownNudgeIdRef.current = nudge.nudge_id;
        trackEvent(TELEMETRY_EVENT_NUDGE_SHOWN, {
            conversation_id: activeConversationId,
            metadata: { nudge_type: nudge.nudge_type, reason: nudge.reason }
        });
    }, [trainingMode, streamState.pendingNudge, activeConversationId]);
    useEffect(() => {
        if (trainingMode || !streamState.pendingNudge)
            return;
        clearNudge();
    }, [trainingMode, streamState.pendingNudge, clearNudge]);
    const isWaitingApproval = streamState.responseState === 'waiting_approval';
    // Passive reconnect: if SSE is dead/dying for an existing conversation, kick off
    // a reconnect in parallel with the send. The orchestrator's per-turn buffer means
    // the response will replay even if SSE re-opens a moment after the POST lands.
    const ensureStreamConnected = useCallback(() => {
        if (!activeConversationId)
            return;
        if (connectionState === 'connected' || connectionState === 'connecting')
            return;
        void reconnectStream();
    }, [activeConversationId, connectionState, reconnectStream]);
    // Passive reconnect on tab return / window focus.
    // The widget's SSE connection often dies silently when the tab is backgrounded
    // (browser timer throttling triggers the 45 s heartbeat timeout). When the user
    // comes back, kick off a reconnect immediately so the widget is live by the time
    // they type — instead of waiting for them to discover the "Try reconnecting" banner.
    useEffect(() => {
        if (!activeConversationId)
            return undefined;
        const reconnectIfNeeded = () => {
            if (typeof document !== 'undefined' && document.hidden)
                return;
            if (connectionState === 'connected' || connectionState === 'connecting')
                return;
            void reconnectStream();
        };
        document.addEventListener('visibilitychange', reconnectIfNeeded);
        window.addEventListener('focus', reconnectIfNeeded);
        return () => {
            document.removeEventListener('visibilitychange', reconnectIfNeeded);
            window.removeEventListener('focus', reconnectIfNeeded);
        };
    }, [activeConversationId, connectionState, reconnectStream]);
    const { approve: rawApprove, reject: rawReject, error: confirmationError } = useConfirmation({
        conversationId: activeConversationId,
        pageContext: effectivePageContext
    });
    // Wrap approve/reject to auto-detect plan vs single-action approval
    const approve = useCallback((id) => {
        var _a, _b;
        const isPlan = ((_b = (_a = streamState.pendingApproval) === null || _a === void 0 ? void 0 : _a.confirmation) === null || _b === void 0 ? void 0 : _b.type) === 'execution_plan';
        return rawApprove(id, { isPlan });
    }, [rawApprove, streamState.pendingApproval]);
    const reject = useCallback((id) => {
        var _a, _b;
        const isPlan = ((_b = (_a = streamState.pendingApproval) === null || _a === void 0 ? void 0 : _a.confirmation) === null || _b === void 0 ? void 0 : _b.type) === 'execution_plan';
        return rawReject(id, { isPlan });
    }, [rawReject, streamState.pendingApproval]);
    useEffect(() => {
        if (streamState.isStreaming && !streamingMessageId && streamState.responseId) {
            setStreamingMessageId(streamState.responseId);
        }
    }, [streamState.isStreaming, streamState.responseId, streamingMessageId]);
    const liveAgentMessage = useMemo(() => (streamingMessageId ? buildLiveAgentMessage(streamingMessageId, streamState) : null), [streamingMessageId, streamState]);
    const handleTosRequired = useCallback(() => {
        setTosRequiredByServer(true);
    }, []);
    // The orchestrator's idempotency gate deduped a send: it never ran the graph,
    // so nothing new will ever be published for that POST. If the ORIGINAL turn is
    // still running its events are in the per-turn buffer, and re-attaching is the
    // only way this send still renders an answer.
    const handleDuplicateSend = useCallback((responseId) => {
        console.warn('[AIChatWidget] Send deduplicated by the orchestrator; re-attaching to response', responseId);
        ensureStreamConnected();
    }, [ensureStreamConnected]);
    const { handleSubmit, isSending, resetSending, abortSend, notifyStreamStarted } = useChatInput({
        conversationId: activeConversationId,
        appendMessage,
        pageContext: effectivePageContext,
        // Only an explicit `false` blocks here. Unknown status is handled upstream in
        // handleSendMessage, which surfaces an error rather than dropping the send.
        tosAccepted: (tosStatus === null || tosStatus === void 0 ? void 0 : tosStatus.accepted) !== false,
        onTosRequired: handleTosRequired,
        trainingMode,
        onDuplicateSend: handleDuplicateSend
    });
    // C1: Notify useChatInput when stream_start arrives so isSending clears.
    // responseState transitions to 'receiving' on each stream_start event.
    // The notifyStreamStarted guard (awaitingStreamStartRef) makes this a no-op
    // unless we're actively awaiting stream_start after a POST /messages.
    useEffect(() => {
        if (streamState.responseState === 'receiving') {
            notifyStreamStarted();
        }
    }, [streamState.responseState, notifyStreamStarted]);
    useEffect(() => {
        var _a, _b, _c;
        if (requestedConversationScope === activeConversationScope)
            return;
        conversationScopeGenerationRef.current += 1;
        // Stop any queued-undo poll belonging to the conversation being left —
        // otherwise it keeps hitting the old conversation's status endpoint for up
        // to 15 minutes.
        (_a = undoPollAbortRef.current) === null || _a === void 0 ? void 0 : _a.abort();
        undoPollAbortRef.current = null;
        pendingMessageRef.current = null;
        resetStream();
        resetSending();
        clearMessages();
        fileUpload.clearFiles();
        setStreamingMessageId(null);
        setActiveConversationScope(requestedConversationScope);
        setActiveConversationId((_c = (_b = loadWidgetState(requestedConversationScope)) === null || _b === void 0 ? void 0 : _b.activeSessionId) !== null && _c !== void 0 ? _c : null);
    }, [requestedConversationScope, activeConversationScope, resetStream, resetSending, clearMessages, fileUpload]);
    // Deferred send: when files finish uploading, send the message
    useEffect(() => {
        const pending = pendingMessageRef.current;
        if (!pending || !fileUpload.canSubmit || fileUpload.files.length === 0)
            return;
        pendingMessageRef.current = null;
        const fileIds = fileUpload.getFileIds();
        // Render every settled chip (clean + errored) in the user message bubble
        // so the user can see WHICH files were skipped and hover for the reason.
        // Only the clean IDs go to the orchestrator via fileIds — failed files
        // never reach the agent.
        const settledAttachments = fileUpload.getAllSettledFileRefs();
        if (settledAttachments.length > 0) {
            updateMessage(pending.optimisticMessageId, (msg) => (Object.assign(Object.assign({}, msg), { file_attachments: settledAttachments })));
        }
        if (pending.isNew) {
            const operationGeneration = conversationScopeGenerationRef.current;
            void (() => __awaiter(this, void 0, void 0, function* () {
                try {
                    const chat = yield createChat({
                        message: pending.message,
                        file_ids: fileIds,
                        page_context: effectivePageContext,
                        training_mode: trainingMode
                    });
                    if (conversationScopeGenerationRef.current !== operationGeneration)
                        return;
                    const userMsg = buildUserMessage(pending.message, settledAttachments.length > 0 ? settledAttachments : pending.fileAttachments);
                    seedMessages(chat.id, [userMsg]);
                    clearMessages();
                    setActiveConversationId(chat.id);
                    refetchConversations();
                }
                catch (err) {
                    if (conversationScopeGenerationRef.current !== operationGeneration)
                        return;
                    console.error('[AIChatWidget] Error creating chat with files:', err);
                    appendCreateError(err);
                }
                finally {
                    if (conversationScopeGenerationRef.current === operationGeneration) {
                        fileUpload.clearFiles();
                    }
                }
            }))();
        }
        else if (activeConversationId) {
            ensureStreamConnected();
            void handleSubmit(pending.message, fileIds, true).finally(() => {
                fileUpload.clearFiles();
            });
        }
    }, [fileUpload.canSubmit, fileUpload.files.length]);
    const handleSelectConversation = useCallback((id) => {
        // Deterministically clear the outgoing conversation's cached stream state
        // (isStreaming, in-progress blocks) and the local "sending" flag BEFORE
        // switching. Without this, a conversation left mid-response keeps
        // isStreaming: true in the React Query cache (staleTime: Infinity) —
        // returning to it later renders that stale value immediately, showing
        // "stop" active and the input blocked until a fresh SSE reconnect
        // happens to deliver a stream_end for the (by-then-completed) turn.
        // This is a deliberate close, not a network-drop retry: it does not
        // touch useQuerySubscription's error/backoff state, and the actual SSE
        // connection for the outgoing conversation still tears down on its own
        // via the effect cleanup (queryKey/url change) the moment the id below
        // changes — no explicit abort() needed here.
        resetStream();
        resetSending();
        setActiveConversationId(id);
        setStreamingMessageId(null);
    }, [resetStream, resetSending]);
    const handleDeleteConversation = useCallback((sessionId) => {
        const target = conversations.find((c) => c.sessionId === sessionId);
        if (!target)
            return;
        setPendingDelete({ sessionId, title: target.title });
    }, [conversations]);
    const handleCancelDelete = useCallback(() => {
        if (isDeleting)
            return;
        setPendingDelete(null);
    }, [isDeleting]);
    const handleConfirmDelete = useCallback(() => {
        if (!pendingDelete)
            return;
        const { sessionId } = pendingDelete;
        const target = conversations.find((c) => c.sessionId === sessionId);
        if (!target) {
            setPendingDelete(null);
            return;
        }
        setIsDeleting(true);
        // Optimistically remove from cached conversation list
        const queryKey = ['ai-chat', 'conversations'];
        const previous = queryClient.getQueryData(queryKey);
        queryClient.setQueryData(queryKey, (current) => (current !== null && current !== void 0 ? current : []).filter((c) => c.sessionId !== sessionId));
        // If the active conversation is being deleted, reset to the empty/new view.
        // Track this so we can restore the selection if the API call fails.
        const deletedActiveConversation = activeConversationId === sessionId;
        const operationGeneration = conversationScopeGenerationRef.current;
        if (deletedActiveConversation) {
            // See handleSelectConversation for why the outgoing stream/sending state
            // must be reset here too — deleting the active (possibly still-streaming)
            // conversation is just another way of navigating away from it.
            resetStream();
            resetSending();
            clearMessages();
            fileUpload.clearFiles();
            setActiveConversationId(null);
            setStreamingMessageId(null);
        }
        void (() => __awaiter(this, void 0, void 0, function* () {
            try {
                yield deleteChatSession(target.id);
                refetchConversations();
            }
            catch (err) {
                console.error('[AIChatWidget] Error deleting chat session:', err);
                // Roll back optimistic update on failure
                if (previous)
                    queryClient.setQueryData(queryKey, previous);
                // Restore the active conversation so the user returns to the thread
                // they were on instead of being left on a blank new chat.
                if (deletedActiveConversation && conversationScopeGenerationRef.current === operationGeneration) {
                    setActiveConversationId(sessionId);
                }
            }
            finally {
                setIsDeleting(false);
                setPendingDelete(null);
            }
        }))();
    }, [
        pendingDelete,
        conversations,
        queryClient,
        activeConversationId,
        clearMessages,
        fileUpload,
        refetchConversations,
        resetStream,
        resetSending
    ]);
    const handleNewConversation = useCallback(() => {
        // See handleSelectConversation — starting a new conversation is also
        // navigating away from whatever was previously active.
        resetStream();
        resetSending();
        clearMessages();
        fileUpload.clearFiles();
        setActiveConversationId(null);
        setStreamingMessageId(null);
    }, [resetStream, resetSending, clearMessages, fileUpload]);
    // New-conversation nudge (ipa-training-mode) — accept/dismiss handlers.
    // Fail-open: the ack POST never blocks the local UX (new conversation /
    // banner dismissal always completes even if the ack request fails).
    const handleAcceptNudge = useCallback((nudge) => {
        trackEvent(TELEMETRY_EVENT_NUDGE_ACCEPTED, {
            conversation_id: activeConversationId,
            metadata: { nudge_type: nudge.nudge_type }
        });
        ackNudge({
            nudge_id: nudge.nudge_id,
            nudge_type: nudge.nudge_type,
            action: NUDGE_ACTION_ACCEPTED,
            conversation_id: activeConversationId
        });
        clearNudge();
        handleNewConversation();
    }, [activeConversationId, clearNudge, handleNewConversation]);
    const handleDismissNudge = useCallback((nudge) => {
        trackEvent(TELEMETRY_EVENT_NUDGE_DISMISSED, {
            conversation_id: activeConversationId,
            metadata: { nudge_type: nudge.nudge_type }
        });
        ackNudge({
            nudge_id: nudge.nudge_id,
            nudge_type: nudge.nudge_type,
            action: NUDGE_ACTION_DISMISSED,
            conversation_id: activeConversationId
        });
        clearNudge();
    }, [activeConversationId, clearNudge]);
    const handleSelectAgent = useCallback((agent) => {
        setSelectedAgent(agent.name);
    }, []);
    const handleSelectAgentByName = useCallback((agentName) => {
        setSelectedAgent(agentName);
    }, []);
    // Render a failed create-chat as a plain-English assistant message instead
    // of only logging to the console — otherwise the widget appears to hang with
    // no explanation (e.g. a 422 "message too long"). See describeChatSendError.
    const appendCreateError = useCallback((err) => {
        appendMessage(buildAgentMessage({
            responseId: `create-err-${Date.now()}`,
            conversationId: '',
            agent: DEFAULT_AGENT,
            blocks: [
                {
                    type: 'markdown',
                    block_id: 'create-err-0',
                    block_index: 0,
                    content: describeChatSendError(err)
                }
            ]
        }));
    }, [appendMessage]);
    // Surfaced when TOS acceptance cannot be determined (status request failed).
    // Distinct from appendCreateError: nothing was attempted, so there is no
    // upstream error to describe — the point is to break the silence.
    const appendTosUnavailableError = useCallback(() => {
        appendMessage(buildAgentMessage({
            responseId: `tos-unavailable-${Date.now()}`,
            conversationId: '',
            agent: DEFAULT_AGENT,
            blocks: [
                {
                    type: 'markdown',
                    block_id: 'tos-unavailable-0',
                    block_index: 0,
                    content: TOS_STATUS_UNAVAILABLE_MESSAGE
                }
            ]
        }));
    }, [appendMessage]);
    const handleSendMessage = useCallback((message, attachments) => {
        // /usage slash-command intercept — must run BEFORE the conversation
        // creation branch below; otherwise typing `/usage` on a fresh widget
        // would create a new conversation and hit the office-agent greeting
        // instead of the local usage block. Existing-conversation path also
        // intercepts inside useChatInput.handleSubmit, but hoisting here
        // covers the no-conversation case (first-open).
        if (message.trim().toLowerCase() === '/usage' && (!attachments || attachments.length === 0)) {
            void handleSubmit(message);
            return;
        }
        // Explicit denial → TosModal is already open; the user must act there.
        if (tosGate === 'blocked-awaiting-acceptance')
            return;
        // Status unknown (the check failed — commonly an expired session 401ing).
        // Tell the user instead of dropping the message on the floor: a silent
        // return here made the whole widget look dead while the platform was fine.
        if (tosGate === 'blocked-status-unknown') {
            appendTosUnavailableError();
            return;
        }
        ensureStreamConnected();
        // If attachments are present, upload first then defer send
        if (attachments && attachments.length > 0) {
            const trimmed = message.trim();
            if (!trimmed)
                return; // Require a text message with file uploads
            const fileAttachments = buildOptimisticFileRefs(attachments);
            const userMsg = buildUserMessage(trimmed, fileAttachments);
            appendMessage(userMsg);
            pendingMessageRef.current = {
                message: trimmed,
                isNew: !activeConversationId,
                fileAttachments,
                optimisticMessageId: userMsg.id
            };
            fileUpload.uploadFiles(attachments);
            return;
        }
        if (!activeConversationId) {
            const userMsg = buildUserMessage(message);
            appendMessage(userMsg);
            const operationGeneration = conversationScopeGenerationRef.current;
            void (() => __awaiter(this, void 0, void 0, function* () {
                try {
                    const chat = yield createChat({ message, page_context: effectivePageContext, training_mode: trainingMode });
                    if (conversationScopeGenerationRef.current !== operationGeneration)
                        return;
                    const threadId = chat.id;
                    seedMessages(threadId, [userMsg]);
                    clearMessages();
                    setActiveConversationId(threadId);
                    refetchConversations();
                }
                catch (err) {
                    if (conversationScopeGenerationRef.current !== operationGeneration)
                        return;
                    console.error('[AIChatWidget] Error creating chat:', err);
                    appendCreateError(err);
                }
            }))();
            return;
        }
        // For existing conversations, useChatInput handles the optimistic update
        void handleSubmit(message);
    }, [
        activeConversationId,
        tosGate,
        appendTosUnavailableError,
        handleSubmit,
        appendMessage,
        seedMessages,
        refetchConversations,
        fileUpload,
        ensureStreamConnected,
        effectivePageContext,
        clearMessages,
        trainingMode
    ]);
    // Cross-island "add documents to the knowledge base" hand-off. Another island
    // (the AI Rules & Knowledge → Documents page) dispatches a command with the
    // picked files; we open the widget, reset to a fresh conversation, and queue
    // the send. The actual send fires from the effect below once the conversation
    // has been cleared, so the message always starts a NEW chat.
    useEffect(() => {
        const unsubscribe = subscribeWidgetCommands((command) => {
            if (command.type !== 'ingest-documents' || command.files.length === 0)
                return;
            setIsOpen(true);
            // Reset to a fresh conversation WITHOUT touching fileUpload: handleSendMessage
            // (fired from the effect below) is what uploads command.files, so calling
            // fileUpload.clearFiles() here would race the upload across React's unbatched
            // native-event renders and drop the attachment (IP-14877 QA fix). Messages +
            // stream state are safe to reset now.
            resetStream();
            resetSending();
            clearMessages();
            setStreamingMessageId(null);
            setActiveConversationId(null);
            setQueuedIngest({ message: command.message, files: command.files });
        });
        return unsubscribe;
    }, [clearMessages, resetStream, resetSending]);
    // Fire the queued ingest once (a) the active conversation is cleared AND
    // (b) the AI JWT has been fetched. The token is only fetched when the widget
    // opens (useAiToken enabled: isOpen), so a hand-off that opens the widget and
    // immediately uploads would 401 on the presign call before the token lands.
    // Waiting for `aiToken` closes that race (IP-14877 QA fix).
    useEffect(() => {
        if (!queuedIngest || activeConversationId !== null || !aiToken)
            return;
        const { message, files } = queuedIngest;
        setQueuedIngest(null);
        handleSendMessage(message, files);
    }, [queuedIngest, activeConversationId, aiToken, handleSendMessage]);
    const handleFeedback = useCallback((responseId, turnNumber, rating, comment) => {
        if (!activeConversationId || !(responseId === null || responseId === void 0 ? void 0 : responseId.trim()))
            return;
        if (!Number.isFinite(turnNumber) || turnNumber < 1)
            return;
        const payload = {
            response_id: responseId,
            turn_number: turnNumber,
            rating
        };
        if (comment)
            payload.comment = comment;
        void submitFeedback(activeConversationId, payload);
    }, [activeConversationId]);
    const handleStop = useCallback(() => {
        // Do NOT call abortStream() here — it permanently kills the SSE connection.
        // The server-side cancel endpoint publishes a stream_end event through the
        // still-alive SSE connection, which transitions state to 'complete' and
        // re-enables input naturally. Aborting the connection prevents that event
        // from arriving and leaves the session in a broken state for follow-up messages.
        resetStream();
        resetSending();
        // M2: abort the in-flight POST /messages so the server never processes a
        // duplicate send when the user stops before stream_start arrives.
        abortSend();
        if (activeConversationId) {
            void cancelResponse(activeConversationId).catch(() => {
                // Cancel is best-effort; the SSE connection remains alive to receive
                // the server's stream_end event
            });
        }
        if (streamingMessageId) {
            const cancelledMessage = buildCancelledMessage(streamState);
            if (cancelledMessage) {
                appendMessage(cancelledMessage);
            }
        }
        // C2: Only restore the user's prompt when NOT in waiting_approval.
        // Replaying the message while a ConfirmationBlock is active triggers a
        // phantom second invocation — the primary iOS write-op duplicate POST vector.
        if (!isWaitingApproval) {
            const lastUserMessage = [...historyMessages].reverse().find((m) => m.role === 'user');
            if (lastUserMessage === null || lastUserMessage === void 0 ? void 0 : lastUserMessage.content) {
                const text = extractTextFromContent(lastUserMessage.content);
                setRetryPrompt(text);
            }
        }
        setStreamingMessageId(null);
    }, [
        resetStream,
        resetSending,
        abortSend,
        activeConversationId,
        streamingMessageId,
        streamState,
        appendMessage,
        isWaitingApproval,
        historyMessages,
        setRetryPrompt
    ]);
    const handleUndo = useCallback((actionId, onProgress) => __awaiter(this, void 0, void 0, function* () {
        var _a;
        if (!activeConversationId) {
            console.error('[AIChatWidget] Cannot undo: no active conversation');
            return;
        }
        // The page as it is now, not as it was at the write (IP-16492).
        const result = yield submitUndo({ conversation_id: activeConversationId, action_id: actionId }, effectivePageContext);
        // A single-action undo is done when the POST returns. A bulk undo is only
        // ACCEPTED at that point — the revert runs on Rails' queue (IP-15237
        // follow-up), so resolving here would flip the button to "Undone" while
        // hundreds of records are still being restored. Keep the promise pending
        // until the run reaches a terminal state; UndoButton renders "Undoing..."
        // for exactly that window and only shows "Undone" when this resolves.
        if ((result === null || result === void 0 ? void 0 : result.status) !== 'queued' || !result.undo_bulk_operation_id) {
            return;
        }
        (_a = undoPollAbortRef.current) === null || _a === void 0 ? void 0 : _a.abort();
        const controller = new AbortController();
        undoPollAbortRef.current = controller;
        try {
            yield pollUndoUntilSettled(activeConversationId, result.undo_bulk_operation_id, {
                signal: controller.signal,
                onProgress
            });
        }
        finally {
            if (undoPollAbortRef.current === controller)
                undoPollAbortRef.current = null;
        }
    }), [activeConversationId, effectivePageContext]);
    // Abort a queued-undo poll if the widget unmounts mid-flight.
    useEffect(() => () => {
        var _a;
        (_a = undoPollAbortRef.current) === null || _a === void 0 ? void 0 : _a.abort();
    }, []);
    const handleRetry = useCallback(() => {
        const lastUserMessage = [...historyMessages].reverse().find((m) => m.role === 'user');
        if (lastUserMessage === null || lastUserMessage === void 0 ? void 0 : lastUserMessage.content) {
            const text = extractTextFromContent(lastUserMessage.content);
            setRetryPrompt(text);
        }
    }, [historyMessages, setRetryPrompt]);
    const handleSelectPrompt = useCallback((prompt) => {
        setRetryPrompt(prompt);
        triggerFocusInput();
    }, [setRetryPrompt, triggerFocusInput]);
    // Telemetry only — chip click-through by page type is a relevance signal
    // for the contextual-chips feature. Does not affect send behavior.
    const handleChipSelect = useCallback((suggestion) => {
        var _a;
        trackEvent(TELEMETRY_EVENT_CHIP_CLICK, {
            conversation_id: activeConversationId,
            metadata: { page_type: (_a = resolvePageType(effectivePageContext)) !== null && _a !== void 0 ? _a : 'unknown', label: suggestion.label }
        });
    }, [activeConversationId, effectivePageContext]);
    const handleBlockAction = useCallback((action, params, label) => {
        switch (action) {
            case 'query':
                handleSendMessage(label);
                break;
            case 'navigate':
                if (params.url)
                    window.location.href = params.url;
                break;
            case 'open_intercom':
                if (typeof window.Intercom === 'function') {
                    const prefill = typeof params.prefill_message === 'string' ? params.prefill_message : '';
                    setIsOpen(false);
                    window.setTimeout(() => {
                        var _a;
                        (_a = window.Intercom) === null || _a === void 0 ? void 0 : _a.call(window, 'showNewMessage', prefill);
                    }, 0);
                }
                else {
                    console.warn('[AIChatWidget] open_intercom action fired but window.Intercom is not available');
                }
                break;
            case 'export':
            case 'upgrade':
            default:
                break;
        }
    }, [handleSendMessage]);
    const handleSuggestedAction = useCallback((action) => handleBlockAction(action.action, action.params, action.label), [handleBlockAction]);
    const handleAcceptTos = useCallback(() => {
        acceptTosMutation.mutate(undefined, {
            onSuccess: () => setTosRequiredByServer(false)
        });
    }, [acceptTosMutation]);
    const handleOpenPromptLibrary = useCallback(() => {
        setIsPromptLibraryOpen(true);
    }, []);
    const handleClosePromptLibrary = useCallback(() => {
        setIsPromptLibraryOpen(false);
    }, []);
    const handleUsePrompt = useCallback((prompt) => {
        setIsPromptLibraryOpen(false);
        setRetryPrompt(prompt);
    }, [setRetryPrompt]);
    const drawer = (React.createElement(ChatWidgetDrawer, { conversations: conversations, activeConversationId: activeConversationId, isLoading: isLoadingHistory, defaultCollapsed: presentationMode, 
        // Only a known denial disables browsing. An unknown status must not
        // disable the drawer — that turned a failed request into a dead UI.
        disabled: (tosStatus === null || tosStatus === void 0 ? void 0 : tosStatus.accepted) === false, onSelectConversation: handleSelectConversation, onNewConversation: handleNewConversation, onSelectAssistant: handleSelectAgent, onPromptLibrary: handleOpenPromptLibrary, onDeleteConversation: handleDeleteConversation, promptCoachEnabled: trainingMode, onTogglePromptCoach: handleToggleTrainingMode, promptCoachButtonRef: trainingModeBtnRef }));
    return (React.createElement(React.Fragment, null,
        !isOpen && React.createElement(AITriggerButton, { onClick: () => setIsOpen(true) }),
        React.createElement(Brushaway, { minWidth: presentationMode ? 800 : 960, drawer: drawer, drawerPosition: "left", controlledOpen: isOpen, onClose: () => setIsOpen(false) },
            React.createElement(BrushawayHeader, { title: translate('title', { namespace: HEADER_NAMESPACE }), secondaryActions: React.createElement(React.Fragment, null,
                    React.createElement(AiConfigureMenu, { items: buildAiConfigureItems('widget'), visible: (isAdmin !== null && isAdmin !== void 0 ? isAdmin : false) && host !== 'technician_portal', variant: "icon" }),
                    React.createElement(Button, { className: "ai-chat-widget__new-conversation-btn", onClick: handleNewConversation, "aria-label": translate('new_conversation', { namespace: HEADER_NAMESPACE }), title: translate('new_conversation', { namespace: HEADER_NAMESPACE }) },
                        React.createElement("i", { className: "fa-light fa-pen-to-square", "aria-hidden": "true" }))) }),
            React.createElement(BrushawayBody, { className: "ai-chat-widget__body" },
                React.createElement("div", { ref: brushawayRef, style: { position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' } },
                    React.createElement(ChatWidgetConversation, { messages: historyMessages, userName: userName, isStreaming: streamState.isStreaming || isSending || fileUpload.isUploading, isWaitingApproval: isWaitingApproval, pendingApprovalId: (_c = (_b = streamState.pendingApproval) === null || _b === void 0 ? void 0 : _b.approval_id) !== null && _c !== void 0 ? _c : null, isLoadingMessages: isLoadingMessages, liveAgentMessage: liveAgentMessage, hasNextPage: hasNextPage, isFetchingNextPage: isFetchingNextPage, suggestedActions: streamState.suggestedActions, progressMessage: streamState.progressMessage, progressDetail: streamState.progressDetail, onFetchNextPage: fetchNextPage, onSendMessage: handleSendMessage, onSuggestedAction: handleSuggestedAction, onBlockAction: handleBlockAction, onConfirm: approve, onCancel: reject, onUndo: handleUndo, onFeedback: handleFeedback, onRetry: handleRetry, onSelectAgent: handleSelectAgentByName, onSelectPrompt: handleSelectPrompt, promptSuggestions: effectiveSuggestions, promptSuggestionsLabel: effectiveSuggestionsLabel, onChipSelect: handleChipSelect, trainingEnabled: trainingMode, pageContext: effectivePageContext, conversationId: activeConversationId, pendingNudge: streamState.pendingNudge, onAcceptNudge: handleAcceptNudge, onDismissNudge: handleDismissNudge, connectionState: connectionState, reconnectAttempts: reconnectAttempts, wasAuthRefresh: wasAuthRefresh, onReconnect: () => {
                            void reconnectStream();
                        }, onStop: handleStop, isInputDisabledForTos: isTosModalOpen, allowAttachments: allowAttachments }),
                    confirmationError && isWaitingApproval && (React.createElement("div", { role: "alert", "aria-live": "assertive", style: {
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 12px',
                            background: 'var(--color-danger-surface, #fff1f0)',
                            borderTop: '1px solid var(--color-danger-border, #ffa39e)',
                            fontSize: '12px',
                            color: 'var(--color-danger-text, #cf1322)'
                        } },
                        React.createElement("i", { className: "fa-light fa-triangle-exclamation", "aria-hidden": "true" }),
                        React.createElement("span", { style: { flex: 1 } },
                            "Approval failed \u2014 please try again.",
                            confirmationError.message ? ` (${confirmationError.message})` : ''))),
                    React.createElement(TosModal, { isOpen: isTosModalOpen, onAccept: handleAcceptTos, isSubmitting: isTosAcceptSubmitting, error: acceptTosMutation.isError ? translate('generic_error', { namespace: TOS_NAMESPACE }) : null, anchor: (_d = brushawayRef.current) !== null && _d !== void 0 ? _d : undefined }),
                    React.createElement(CoachWelcomeModal, { isOpen: isCoachWelcomeOpen, onComplete: handleCompleteCoachWelcome, onDismiss: handleDismissCoachWelcome, anchor: (_e = brushawayRef.current) !== null && _e !== void 0 ? _e : undefined })))),
        React.createElement(PromptLibraryModal, { isOpen: isPromptLibraryOpen, onClose: handleClosePromptLibrary, onUsePrompt: handleUsePrompt, library: promptLibrary }),
        React.createElement(ConfirmDialog, { isOpen: pendingDelete !== null, title: translate('title', { namespace: DELETE_CONVERSATION_NAMESPACE }), description: pendingDelete
                ? translate('description', { namespace: DELETE_CONVERSATION_NAMESPACE, title: pendingDelete.title })
                : '', confirmLabel: translate('delete', { namespace: 'features.commons' }), confirmVariant: "primary", loading: isDeleting, onConfirm: handleConfirmDelete, onCancel: handleCancelDelete })));
}
export function AIChatWidget({ userName, defaultOpen, presentationMode, pageContext, host = 'office', coachWelcomeSeen, isAdmin }) {
    return (React.createElement(ChatWidgetContextProvider, null,
        React.createElement(AIChatWidgetInner, { userName: userName, defaultOpen: defaultOpen, presentationMode: presentationMode, pageContext: pageContext, host: host, coachWelcomeSeen: coachWelcomeSeen, isAdmin: isAdmin })));
}
